import type { ListResponse } from 'Services/ServiceProviderManagement/types'
import type { LoadingStates } from 'Store'
import { createAsyncThunkWithErrorHandling, getAxiosInstance } from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createSlice } from '@reduxjs/toolkit'

export enum ServiceProviderTabType {
  DETAILS = 'details',
  SALES_POINTS = 'sales_points',
  ALLOWED_API_CLIENTS = 'allowed_api_clients'
}

export interface ServiceProvider {
  owner_client: string
  client_read: string[]
  client_write: string[]
  service_provider_id: string
  pta: string
  type: ServiceProviderType
  start_date: Date
  active: boolean
  activity_end_date: Date | null
  s365t_account_id: string
  name: string
  customer_reference_name: string
  business_id: string
  billed_at_sales_point_level: boolean
  transfer_sales_to_financial_system: boolean
  register_file_path: string
  register_file_name: string
  register_ok_file_name: string
  register_not_ok_file_name: string
  vrk_queries_enabled: boolean
  cpu_url: string
  pusatec_channel: string
  created_timestamp: Date
  modified_timestamp: Date
  last_modified_client: string
  source: string
}

export enum ServiceProviderType {
  TRAFFIC_CONTRACTOR = 'TRAFFIC_CONTRACTOR',
  U_TRAFFIC_CONTRACTOR = 'U_TRAFFIC_CONTRACTOR',
  SALES_POINT = 'SALES_POINT',
  SERVICE_POINT = 'SERVICE_POINT',
  HSL_DIGITAL_CHANNEL = 'HSL_DIGITAL_CHANNEL',
  MAAS = 'MAAS',
  OTHER = 'OTHER',
  TICKET_MACHINE = 'TICKET_MACHINE',
  WEBSHOP = 'WEBSHOP',
  MOBILE = 'MOBILE',
  EVENT_TICKET = 'EVENT_TICKET'
}

export interface ServiceProviderState {
  total_results: number
  next_page: string | null
  previous_page: string | null
  serviceProviders: ServiceProvider[]
  allServiceProviders: ServiceProvider[]
  singleServiceProvider: ServiceProvider | null
  loading: LoadingStates
  singleLoading: LoadingStates
  allServiceProvidersLoading: LoadingStates
  deleteLoading: LoadingStates
  submitLoading: LoadingStates
}

const initialState: ServiceProviderState = {
  total_results: 0,
  next_page: null,
  previous_page: null,
  serviceProviders: [],
  allServiceProviders: [],
  singleServiceProvider: null,
  loading: 'idle',
  singleLoading: 'idle',
  allServiceProvidersLoading: 'idle',
  deleteLoading: 'idle',
  submitLoading: 'idle'
}

export interface FetchServiceProviderListParams {
  active?: boolean
  count?: number
  name?: string
  search_text?: string
  sort_by?: string
  sort_order?: string
  start_index?: string
  type?: ServiceProviderType
  bypass_pagination?: boolean
}

const fetchServiceProviders = createAsyncThunkWithErrorHandling<
  ListResponse<ServiceProvider>,
  FetchServiceProviderListParams | undefined
>('serviceProvider/fetchServiceProviders', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<ServiceProvider>>(
    '/api/v1/service-provider/',
    {
      params
    }
  )
  return response.data
})

const fetchAllServiceProviders = createAsyncThunkWithErrorHandling<
  ListResponse<ServiceProvider>,
  FetchServiceProviderListParams | undefined
>('serviceProvider/fetchAllServiceProviders', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<ServiceProvider>>(
    '/api/v1/service-provider/',
    {
      params
    }
  )
  return response.data
})

const fetchSingleServiceProvider = createAsyncThunkWithErrorHandling<
  ServiceProvider,
  { id: string }
>('serviceProvider/fetchSingleServiceProvider', async data => {
  const response = await getAxiosInstance().get<ServiceProvider>(
    `/api/v1/service-provider/${data.id}/`
  )
  return response.data
})

const createSingleServiceProvider = createAsyncThunkWithErrorHandling<
  ServiceProvider,
  Partial<ServiceProvider>
>('serviceProvider/createSingleServiceProvider', async data => {
  const response = await getAxiosInstance().post<ServiceProvider>('/api/v1/service-provider/', data)
  return response.data
})

const updateSingleServiceProvider = createAsyncThunkWithErrorHandling<
  ServiceProvider,
  { id: string } & Partial<ServiceProvider>
>('serviceProvider/updateSingleServiceProvider', async data => {
  const { id, ...fields } = data
  const updatedFields = {
    ...fields,
    // activity_end_date has to be null when updating passive service provider
    activity_end_date: !fields.active && fields.activity_end_date ? null : fields.activity_end_date
  }
  const response = await getAxiosInstance().put<ServiceProvider>(
    `/api/v1/service-provider/${id}/`,
    updatedFields
  )
  return response.data
})

type ServiceProviderDeleteResponse = Record<string, never>

const deleteSingleServiceProvider = createAsyncThunkWithErrorHandling<
  ServiceProviderDeleteResponse,
  { id: string }
>('serviceProvider/deleteSingleServiceProvider', async data => {
  const { id } = data
  await getAxiosInstance().delete(`/api/v1/service-provider/${id}/`)
  return {}
})

const genericActions = actionGenerator('serviceProvider')

export const serviceProviderSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      /* Service provider list */
      .addCase(fetchServiceProviders.fulfilled, (state, { payload }) => {
        state.serviceProviders = payload.resources
        state.total_results = payload.total_results
        state.next_page = payload.next_page
        state.previous_page = payload.previous_page
        state.loading = 'fulfilled'
      })
      .addCase(fetchServiceProviders.pending, state => {
        state.serviceProviders = initialState.serviceProviders
        state.total_results = initialState.total_results
        state.next_page = initialState.next_page
        state.previous_page = initialState.previous_page
        state.loading = 'pending'
      })
      .addCase(fetchServiceProviders.rejected, state => {
        state.loading = 'rejected'
      })

      /* All service providers */
      .addCase(fetchAllServiceProviders.fulfilled, (state, { payload }) => {
        state.allServiceProviders = payload.resources
        state.total_results = payload.total_results
        state.allServiceProvidersLoading = 'fulfilled'
      })
      .addCase(fetchAllServiceProviders.pending, state => {
        state.allServiceProviders = initialState.serviceProviders
        state.total_results = initialState.total_results
        state.allServiceProvidersLoading = 'pending'
      })
      .addCase(fetchAllServiceProviders.rejected, state => {
        state.allServiceProvidersLoading = 'rejected'
      })

      /* Single service provider fetch */
      .addCase(fetchSingleServiceProvider.fulfilled, (state, { payload }) => {
        state.singleServiceProvider = payload
        state.singleLoading = 'fulfilled'
      })
      .addCase(fetchSingleServiceProvider.pending, state => {
        state.singleServiceProvider = initialState.singleServiceProvider
        state.singleLoading = 'pending'
      })
      .addCase(fetchSingleServiceProvider.rejected, state => {
        state.singleLoading = 'rejected'
      })

      /* Single service provider update */
      .addCase(updateSingleServiceProvider.fulfilled, (state, { payload }) => {
        state.singleServiceProvider = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(updateSingleServiceProvider.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(updateSingleServiceProvider.rejected, state => {
        state.submitLoading = 'rejected'
      })

      /* Single service provider create */
      .addCase(createSingleServiceProvider.fulfilled, (state, { payload }) => {
        state.singleServiceProvider = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createSingleServiceProvider.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(createSingleServiceProvider.rejected, state => {
        state.submitLoading = 'rejected'
      })

      /* Single service provider delete */
      .addCase(deleteSingleServiceProvider.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteSingleServiceProvider.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteSingleServiceProvider.rejected, state => {
        state.deleteLoading = 'rejected'
      })
  }
})

export default serviceProviderSlice.reducer

export {
  createSingleServiceProvider,
  deleteSingleServiceProvider,
  fetchAllServiceProviders,
  fetchServiceProviders,
  fetchSingleServiceProvider,
  updateSingleServiceProvider
}
